import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  I2gDialogService,
  I2gInputFieldAutocompleteType,
  ISelectOption,
} from 'i2g-ng-ui';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { buildDialogTrackingObject } from '../../../tracking/tracking-object.builder';
import { List } from 'immutable';
import { SetPhoneDialogService } from './set-phone-dialog.service';
import { twilioCountries } from 'i2g-rosetta';
import { GlobalBannerTypesEnum } from '../../../shared/components/global-banner/global-banner.enum';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { I2gNgTrackingService } from 'i2g-ng-tracking';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';

@Component({
  selector: 'app-change-email-dialog',
  templateUrl: './set-phone-dialog.component.html',
  styleUrls: ['./set-phone-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SetPhoneDialogService,
    I2gNgTrackingService.withObject(
      buildDialogTrackingObject('set_phone_dialog')
    ),
  ],
})
export class SetPhoneDialogComponent implements OnInit {
  static isDismissible = true;

  @Input() title = 'SETTINGS.UPDATE_EMAIL_DIALOG.TITLE_0';
  @Input() body = 'SETTINGS.UPDATE_EMAIL_DIALOG.DESCRIPTION_0';
  @Input() allowCountryChange = true;
  @Input() defaultCountry = 'US';
  @Input() phoneNumberPattern: string | RegExp = /^[0-9()\- ]+$/;

  notification:
    | 'SMS_RATE_LIMIT'
    | 'CODE_BLOCKED'
    | 'SMS_SENT'
    | 'SMS_ALREADY_SENT' = null;
  isProcessing = false;
  resendCodeAttempts = 0;
  phoneWithDialingCode: string;

  phoneControl: FormControl;
  countryControl: FormControl;
  smsOrVoiceControl: FormControl;
  codeControl: FormControl;

  phoneFocusTrigger: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );
  codeFocusTrigger: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  countryAutocompleteOptions: List<ISelectOption>;

  activeScreen = 0; // 0 -> enter mobile, 1 -> verify code

  I2gInputFieldAutocompleteType = I2gInputFieldAutocompleteType;

  constructor(
    private _dialogService: I2gDialogService,
    private _translateService: TranslateService,
    private _setPhoneDialogService: SetPhoneDialogService,
    private _globalBannerService: GlobalBannerService,
    private _changeDetector: ChangeDetectorRef,
    private _settingsDataStore: SettingsDataStore
  ) {}

  ngOnInit() {
    this.countryControl = new FormControl(this.defaultCountry);

    this._settingsDataStore.getSettings().subscribe((settings) => {
      const companyCountry = settings.companySettings.country;
      const defaultCountry =
        twilioCountries.default.find(
          (twilioCountry) => twilioCountry.a2 === companyCountry
        )?.a2 || this.defaultCountry;
      this.countryControl.setValue(defaultCountry);
    });
    this.phoneControl = new FormControl('', [
      Validators.required,
      Validators.pattern(this.phoneNumberPattern),
    ]);
    this.smsOrVoiceControl = new FormControl('sms', [Validators.required]);
    this.codeControl = new FormControl('', [Validators.required]);

    this.countryAutocompleteOptions = List(
      twilioCountries.default.map((c) => ({
        key: `${this._translateService.instant(`COUNTRIES.${c.a2}`)} ${
          c.dialCode
        }`,
        value: c.a2,
      }))
    );
  }

  onConfirmClicked() {
    this.isProcessing = true;
    this._setPhoneDialogService.confirmCode(this.codeControl.value).subscribe(
      () => {
        this.isProcessing = false;
        this._dialogService.closeDialog();
        this._setPhoneDialogService.refreshPhone().subscribe();
        this._globalBannerService.showBanner({
          type: GlobalBannerTypesEnum.info,
          message: this._translateService.instant(
            'SETTINGS.UPDATE_EMAIL_DIALOG.MOBILE_UPDATED'
          ),
        });
        this._changeDetector.detectChanges();
      },
      ({ error, status }) => {
        this.isProcessing = false;
        const description = error.error_description;
        if (status === 429) {
          this.notification = 'CODE_BLOCKED';
        }
        if (description === 'mfa_token is expired') {
          this.codeControl.setErrors({ expired: true });
        } else if (description === 'Invalid binding_code.') {
          this.codeControl.setErrors({ invalid: true });
        }
        this.codeFocusTrigger.next(true);
        this._changeDetector.detectChanges();
      }
    );
  }

  onBackClicked() {
    this.resendCodeAttempts = 0;
    this.notification = null;
    this.activeScreen = 0;
  }

  resendSms() {
    if (this.resendCodeAttempts === 0) {
      this._setPhoneDialogService
        .validatePhoneNumber(
          this.phoneWithDialingCode,
          this.countryControl.value
        )
        .subscribe(
          () => {
            this.notification = 'SMS_SENT';
            this._changeDetector.detectChanges();
          },
          (error) => {
            this.handleValidatePhoneError(error);
          }
        );
    } else {
      this.notification = 'SMS_ALREADY_SENT';
    }
    this.resendCodeAttempts++;
  }

  handleValidatePhoneError(error) {
    this.isProcessing = false;
    if (error.status === 400) {
      this.phoneControl.setErrors({ pattern: true });
      this.phoneFocusTrigger.next(true);
    } else if (error.status === 429) {
      this.notification = 'SMS_RATE_LIMIT';
    } else {
      // TODO: something went wrong toast?
    }
    this._changeDetector.detectChanges();
  }

  onNextClicked() {
    const dialCodeRaw = twilioCountries.default.filter(
      (country) => country.a2 === this.countryControl.value
    )[0].dialCode;
    const dialCode = dialCodeRaw.replace(/^(0*)/, '');
    this.phoneWithDialingCode = dialCode + this.phoneControl.value;

    this._setPhoneDialogService
      .validatePhoneNumber(this.phoneWithDialingCode, this.countryControl.value)
      .subscribe(
        () => {
          this.isProcessing = false;
          this.activeScreen = 1;
          this._changeDetector.detectChanges();
        },
        (error) => {
          this.handleValidatePhoneError(error);
        }
      );

    this.isProcessing = true;
  }

  onCancelClicked() {
    this._dialogService.closeDialog();
  }

  handleEnterPressed(event: KeyboardEvent, formControl: FormControl, callback) {
    if (event.key === 'Enter' && formControl.valid) {
      callback();
    }
  }
}
